import {keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

import {useToast} from "@/hooks/use-toast";
import * as apiClient from "@/lib/api";
import {ApiError} from "@/lib/api";
import {useQueryResetWithRefresh} from "@/lib/hooks/query-refetch-with-reset";
import {buildQueryParamsStringWithFilters} from "@/lib/utils";
import {
  InviteHistoryApiResponse,
  InviteHistoryCvsData,
  InviteHistoryData
} from "@/modules/invite-feedback/history/types";
import {FetchParams} from "@/types/search";
import {QUERY_KEY_INVITE_HISTORY} from "@/utils/keys";

function fetchInviteHistoryData(fetchParams: FetchParams): Promise<InviteHistoryApiResponse> {
  return apiClient.get(`/firm/review-invitations?${buildQueryParamsStringWithFilters(fetchParams, {to: undefined, from: undefined}, null)}`, true)
}

export const inviteHistoryQueryOptions = (
  fetchParams: FetchParams
) =>
  queryOptions({
    queryKey: [QUERY_KEY_INVITE_HISTORY, fetchParams],
    queryFn: () => fetchInviteHistoryData(fetchParams),
    placeholderData: keepPreviousData
  });

export const useInviteHistory = (fetchParams: FetchParams) => {
  const queryOptions = inviteHistoryQueryOptions(fetchParams)
  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data?.data ?? [],
    total: query.data?.aggregate_data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

export function updateReviewInviteSendReminder(record: InviteHistoryData) {
  return apiClient.put(`/firm/review-invitations/toggle-send-reminder/${record.id}`, record)
}
export const useReviewInviteSendReminderMutation = (onSettled: () => void, onError: () => void) => {
  const queryClient = useQueryClient();
  const {toast} = useToast()

  return useMutation({
    mutationFn: async (record: InviteHistoryData) => updateReviewInviteSendReminder(record),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [QUERY_KEY_INVITE_HISTORY]});
      toast({description: 'Reminder updated with success.', variant: 'mint'})
    },
    onError: (e: unknown) => {
      onError()
      if (e instanceof ApiError && e.message) {
        toast({description: e.message, variant: 'red'})
      } else {
        toast({description: 'Error updating reminder, please try again'})
      }
    },
    onSettled
  })
}

export function fetchCsvDownloadData(fetchParams: FetchParams): Promise<InviteHistoryCvsData[]> {
  return apiClient.get(`/firm/review-invitations/csv?${buildQueryParamsStringWithFilters(fetchParams, {to: undefined, from: undefined}, null)}`, false)
}

